import { useState, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useToast } from '@practium/ui';
import type { FormSelectOption } from '@practium/ui';
import { useCreateUser, useUpdateUser, useUpdateUserPlan, useGetManagementPlans } from '@practium/features/api';
import type { SystemUserFormData } from '../types';
import { SystemUserSchema, SystemUserEditSchema } from '../schemas/system-user.schema';
import { SYSTEM_USER_FIELD_LABELS, DEFAULT_SYSTEM_USER_FORM } from '../constants/system-user.constants';

export interface UseSystemUserFormOptions {
	initialData?: SystemUserFormData;
	onSubmit: () => void;
}

export const useSystemUserForm = ({ initialData, onSubmit }: UseSystemUserFormOptions) => {
	const toast = useToast();
	const [searchParams] = useSearchParams();
	const routeParams = useParams<{ id?: string }>();
	const userId = searchParams.get('id') ?? routeParams.id;
	const { mutateAsync: createUser } = useCreateUser();
	const { mutateAsync: updateUser } = useUpdateUser();
	const { mutateAsync: updateUserPlan } = useUpdateUserPlan();
	const { data: planDTOs } = useGetManagementPlans();
	const planOptions: FormSelectOption[] = useMemo(
		() => planDTOs.filter(p => p.status === 'active').map(p => ({ value: p._id, label: p.name })),
		[planDTOs]
	);
	const initialForm = initialData
		? { ...DEFAULT_SYSTEM_USER_FORM, ...initialData }
		: { ...DEFAULT_SYSTEM_USER_FORM };
	const [form, setForm] = useState<SystemUserFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [isLoading, setIsLoading] = useState(false);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const set = (key: keyof SystemUserFormData, value: string | boolean) =>
		setForm(f => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = userId ? SystemUserEditSchema.safeParse(form) : SystemUserSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach(issue => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [...new Set(result.error.issues.map(i => String(i.path[0] ?? '')).filter(Boolean))];
			const labels = uniquePaths.map(p => SYSTEM_USER_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		setIsLoading(true);
		try {
			if (userId) {
				await updateUser({
					id: userId,
					name: form.name,
					surName: form.surName,
					email: form.email,
					phoneNumber: form.phoneNumber || undefined,
					status: form.status === 'true',
				});
				if (form.planId && form.planId !== initialFormRef.current.planId) {
					await updateUserPlan({ id: userId, plan: form.planId });
				}
			} else {
				await createUser({
					name: form.name,
					surName: form.surName,
					phoneNumber: form.phoneNumber || undefined,
					email: form.email,
					planId: form.planId || undefined,
					status: form.status === 'true',
				});
			}
			toast.success('Usuário salvo com sucesso!');
			onSubmit();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar usuário');
		} finally {
			setIsLoading(false);
		}
	};

	return { form, errors, isLoading, planOptions, isEditMode: !!userId, set, handleSubmit, isDirty };
};
